import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import Layout from "../components/Layout";
import PostCard from "../components/PostCard";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  MessageCircle, Calendar, ArrowLeft, Loader2, 
  User, Phone, MapPin, Briefcase, Heart, Users,
  Cake, Building, Globe, Droplet, Home, Vote
} from "lucide-react";

// Info Item Component - defined outside main component
const InfoItem = ({ icon: Icon, label, value, iconColor = "text-primary" }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`p-2 rounded-lg bg-muted ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium break-words">{value}</p>
      </div>
    </div>
  );
};

const UserProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [userResponse, postsResponse] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get(`/posts/user/${userId}`)
        ]);
        setUser(userResponse.data);
        setPosts(postsResponse.data);
      } catch (_error) {
        toast.error("User not found");
        navigate("/feed");
      } finally {
        setLoading(false);
      }
    };
    
    if (userId === currentUser?.id) {
      navigate("/profile");
    } else {
      fetchUserData();
    }
  }, [userId, currentUser, navigate]);

  const handleMessage = async () => {
    try {
      const response = await api.post("/conversations", { participant_id: userId });
      navigate(`/messages/${response.data.id}`);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!user) return null;

  const getInitials = () => {
    if (user.first_name) {
      return `${user.first_name[0]}${(user.last_name || "L")[0]}`.toUpperCase();
    }
    if (user.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return user.phone?.slice(-2) || "??";
  };

  const getFullName = () => {
    const parts = [];
    if (user.prefix) parts.push(user.prefix + ".");
    if (user.first_name) parts.push(user.first_name);
    if (user.middle_name) parts.push(user.middle_name);
    if (user.last_name) parts.push(user.last_name);
    return parts.join(" ") || user.username || "User";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatJoinDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="user-profile-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Profile Header */}
        <Card className="mb-6" data-testid="user-profile-header">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className="relative">
                <Avatar className="w-28 h-28 text-2xl ring-4 ring-primary/20">
                  <AvatarImage src={user.avatar} alt={getFullName()} />
                  <AvatarFallback className="bg-primary/10 text-primary text-3xl font-bold">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                {user.verification_status === 'verified' && (
                  <div className="absolute -bottom-1 -right-1 p-1 bg-green-500 rounded-full">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold" data-testid="user-fullname">
                      {getFullName()}
                    </h1>
                    {/* Member Code */}
                    {user.member_code && (
                      <Badge variant="secondary" className="mt-1 bg-blue-100 text-blue-700">
                        {user.member_code}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleMessage}
                      className="btn-hover"
                      data-testid="message-user-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/polls')}
                      data-testid="poll-btn"
                    >
                      <Vote className="w-4 h-4 mr-2" />
                      Poll
                    </Button>
                  </div>
                </div>

                {user.bio && (
                  <p className="mt-3 text-muted-foreground" data-testid="user-bio">
                    {user.bio}
                  </p>
                )}

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-4 text-sm text-muted-foreground">
                  {user.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.city && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{user.city}{user.state ? `, ${user.state}` : ''}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {formatJoinDate(user.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Details and Posts */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="details">Profile Details</TabsTrigger>
            <TabsTrigger value="posts">Posts ({posts.length})</TabsTrigger>
          </TabsList>

          {/* Profile Details Tab */}
          <TabsContent value="details" className="space-y-4">
            {/* Personal Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <InfoItem 
                  icon={User} 
                  label="Full Name" 
                  value={getFullName()} 
                />
                <InfoItem 
                  icon={Phone} 
                  label="Phone" 
                  value={user.phone} 
                />
                <InfoItem 
                  icon={Cake} 
                  label="Date of Birth" 
                  value={formatDate(user.date_of_birth)} 
                  iconColor="text-pink-500"
                />
                <InfoItem 
                  icon={User} 
                  label="Gender" 
                  value={user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : null} 
                />
                <InfoItem 
                  icon={Droplet} 
                  label="Blood Group" 
                  value={user.blood_group} 
                  iconColor="text-red-500"
                />
                <InfoItem 
                  icon={Home} 
                  label="Native Place (Rajasthan)" 
                  value={user.native_place} 
                  iconColor="text-orange-500"
                />
              </CardContent>
            </Card>

            {/* Family Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Family Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <InfoItem 
                  icon={Heart} 
                  label="Marital Status" 
                  value={user.marital_status ? user.marital_status.charAt(0).toUpperCase() + user.marital_status.slice(1) : null} 
                  iconColor="text-red-500"
                />
                {user.marital_status === 'married' && (
                  <>
                    <InfoItem 
                      icon={User} 
                      label={user.gender === 'female' ? "Husband's Name" : "Spouse Name"} 
                      value={user.spouse_name} 
                    />
                    <InfoItem 
                      icon={Calendar} 
                      label="Date of Marriage" 
                      value={formatDate(user.date_of_marriage)} 
                      iconColor="text-pink-500"
                    />
                  </>
                )}
                {/* Number of Children and Details - Only show if not single */}
                {user.marital_status && user.marital_status !== "single" && (
                  <>
                    <InfoItem 
                      icon={Users} 
                      label="Number of Children" 
                      value={user.number_of_children > 0 ? user.number_of_children.toString() : "0"} 
                      iconColor="text-blue-500"
                    />
                    {/* Children Details */}
                    {user.children && user.children.length > 0 && (
                      <div className="col-span-full mt-2">
                        <p className="text-sm font-medium text-muted-foreground mb-2">Children:</p>
                        <div className="space-y-2">
                          {user.children.map((child, index) => (
                            <div key={index} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={child.photo} />
                                <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                  {child.name ? child.name[0].toUpperCase() : (index + 1)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{child.name || `Child ${index + 1}`}</p>
                                <p className="text-xs text-muted-foreground">
                                  {child.gender && `${child.gender.charAt(0).toUpperCase() + child.gender.slice(1)}`}
                                  {child.age && ` • Age: ${child.age}`}
                                  {child.marital_status && ` • ${child.marital_status.charAt(0).toUpperCase() + child.marital_status.slice(1)}`}
                                  {child.education && ` • ${child.education}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-500" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(user.address_line1 || user.city) ? (
                  <div className="space-y-1">
                    {user.address_line1 && <p>{user.address_line1}</p>}
                    {user.address_line2 && <p>{user.address_line2}</p>}
                    <p>
                      {[user.city, user.state, user.pincode].filter(Boolean).join(", ")}
                    </p>
                    {user.is_overseas && user.country && (
                      <p className="flex items-center gap-1">
                        <Globe className="w-4 h-4 text-blue-500" />
                        {user.country}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No address information</p>
                )}
              </CardContent>
            </Card>

            {/* Business Information */}
            {(user.occupation || user.company_name) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    Business / Occupation
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <InfoItem 
                    icon={Briefcase} 
                    label="Occupation" 
                    value={user.occupation} 
                    iconColor="text-purple-500"
                  />
                  <InfoItem 
                    icon={Building} 
                    label="Company" 
                    value={user.company_name} 
                    iconColor="text-purple-500"
                  />
                  <InfoItem 
                    icon={User} 
                    label="Designation" 
                    value={user.designation} 
                  />
                  {user.work_address && (
                    <div className="col-span-full">
                      <InfoItem 
                        icon={MapPin} 
                        label="Work Address" 
                        value={user.work_address} 
                        iconColor="text-green-500"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-4">
            {posts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-user-posts">
                <p className="text-lg">No posts yet</p>
              </div>
            ) : (
              posts.map((post, index) => (
                <div 
                  key={post.id} 
                  className="animate-fade-in"
                  style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                >
                  <PostCard 
                    post={post} 
                    onUpdate={handlePostUpdated}
                    hideDelete={true}
                  />
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default UserProfilePage;
